import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Button, Text, Flex, FormControl, FormLabel,
    Select, HStack, Spinner, useTheme,
} from '@chakra-ui/react';
import { MdClear } from 'react-icons/md';
import { FiSearch } from 'react-icons/fi';
import api from '../../../auth/apiClient';
import { UpkeepMapFilters, CoaOption, ActivityOption, EstateOption, CompanyOption } from '../types';
import { UpkeepMapApiService } from '../services/upkeepMapApiService';
import { UPKEEP_MAP_DEFAULTS, MONTH_NAMES } from '../apiConfig';

interface Props {
    filters: UpkeepMapFilters;
    onApply: (filters: UpkeepMapFilters) => void;
    onClear: () => void;
    loading: boolean;
    isOpen: boolean;
}

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 2019 + 2 }, (_, i) => (2020 + i).toString());
const MONTH_OPTIONS = Object.entries(MONTH_NAMES).sort(([a], [b]) => parseInt(a) - parseInt(b));

const FilterUpkeepMap: React.FC<Props> = ({ filters, onApply, onClear, loading, isOpen }) => {
    const theme = useTheme();
    const [local, setLocal] = useState<UpkeepMapFilters>(filters);

    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [allEstates, setAllEstates] = useState<EstateOption[]>([]);
    const [coaList, setCoaList] = useState<CoaOption[]>([]);
    const [activityList, setActivityList] = useState<ActivityOption[]>([]);
    const [loadingCoa, setLoadingCoa] = useState(false);
    const [loadingActivity, setLoadingActivity] = useState(false);

    useEffect(() => {
        const fetchMeta = async () => {
            try {
                const [optRes, estRes] = await Promise.all([
                    api.get('/inspeksi/options'),
                    api.get('/inspeksi/web/estate-db'),
                ]);
                const optData = optRes.data;
                const estData: any[] = estRes.data ?? [];

                setCompanies(optData.companies ?? []);

                const merged: EstateOption[] = estData.map((estDb: any) => {
                    const match = (optData.estates ?? []).find((e: any) => e.name === estDb.name);
                    return {
                        id: estDb.id,
                        name: estDb.name,
                        company_id: estDb.company_id,
                        odoo_id: match?.odoo_id ?? estDb.id,
                    };
                });
                setAllEstates(merged);
            } catch (e) {
                console.error('FilterUpkeepMap: error fetching meta', e);
            }
        };
        fetchMeta();
    }, []);

    useEffect(() => {
        setCoaList([]);
        setActivityList([]);
        setLocal(prev => ({ ...prev, activity_group_id: undefined, activity_id: undefined }));
        if (!local.company_id) return;
        setLoadingCoa(true);
        UpkeepMapApiService.getCoaList(local.company_id)
            .then(setCoaList)
            .catch(console.error)
            .finally(() => setLoadingCoa(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [local.company_id]);

    useEffect(() => {
        setActivityList([]);
        setLocal(prev => ({ ...prev, activity_id: undefined, reference_activity_id: undefined }));
        if (!local.activity_group_id) return;
        setLoadingActivity(true);
        UpkeepMapApiService.getActivityList(local.activity_group_id)
            .then(setActivityList)
            .catch(console.error)
            .finally(() => setLoadingActivity(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [local.activity_group_id]);

    const filteredEstates = useMemo(
        () => allEstates.filter(e => e.company_id === local.company_id),
        [allEstates, local.company_id]
    );

    const set = useCallback(<K extends keyof UpkeepMapFilters>(key: K, val: UpkeepMapFilters[K]) => {
        setLocal(prev => ({ ...prev, [key]: val }));
    }, []);

    const handleApply = () => onApply(local);

    const handleClear = () => {
        const cleared: UpkeepMapFilters = {
            company_id: UPKEEP_MAP_DEFAULTS.COMPANY_ID,
            budget_year: currentYear.toString(),
            budget_month: String(new Date().getMonth() + 1).padStart(2, '0'),
            revision: UPKEEP_MAP_DEFAULTS.REVISION,
        };
        setLocal(cleared);
        setCoaList([]);
        setActivityList([]);
        onClear();
    };


    const inputStyles = {
        size: 'sm' as const,
        borderRadius: 'lg' as const,
        borderColor: 'gray.200',
        _hover: { borderColor: 'gray.300' },
        _focus: {
            borderColor: theme.colors.blue?.[400] ?? 'blue.400',
            boxShadow: `0 0 0 1px ${theme.colors.blue?.[400] ?? '#63b3ed'}`,
        },
    };

    return (
        <Box
            position="fixed"
            left={{ base: isOpen ? '0' : '-100%', md: isOpen ? '0' : '-350px' }}
            top={{ base: '60px', md: '40px' }}
            w={{ base: 'calc(100% - 64px)', md: '350px' }}
            h={{ base: 'calc(100dvh - 60px)', md: 'calc(100dvh - 40px)' }}
            transition="left 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            zIndex={15}
            boxShadow="lg"
            bg="white"
            display="flex"
            flexDirection="column"
            borderRight="1px solid"
            borderColor="gray.200"
        >
            {/* Header */}
            <Box
                px={{ base: 3, md: 4 }}
                py={{ base: 2, md: 3 }}
                // borderBottom="1px solid"
                // borderColor="gray.100"
                bg="white"
                flexShrink={0}
            >
                <Text fontWeight="bold" color="black" fontSize={{ base: 'sm', md: 'md' }}>
                    Peta Rawat
                </Text>
            </Box>

            {/* Scrollable content */}
            <Box
                flex={1}
                overflowY="auto"
                px={{ base: 3, md: 4 }}
                py={{ base: 3, md: 4 }}
                display="flex"
                flexDirection="column"
                gap={{ base: 3, md: 4 }}
            >
                {/* Company */}
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>Company</FormLabel>
                    <Select
                        {...inputStyles}
                        value={local.company_id}
                        onChange={e => set('company_id', Number(e.target.value))}
                    >
                        {companies.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Select>
                </FormControl>

                {/* Estate */}
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>Estate</FormLabel>
                    <Select
                        {...inputStyles}
                        placeholder="Semua Estate"
                        value={local.estate_id ?? ''}
                        onChange={e => set('estate_id', e.target.value ? Number(e.target.value) : undefined)}
                    >
                        {filteredEstates.map(e => (
                            <option key={e.id} value={e.odoo_id}>{e.name}</option>
                        ))}
                    </Select>
                </FormControl>

                {/* Activity Group (COA) */}
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>
                        <HStack spacing={1}>
                            <Text>Activity Group</Text>
                            {loadingCoa && <Spinner size="xs" />}
                        </HStack>
                    </FormLabel>
                    <Select
                        {...inputStyles}
                        placeholder="Pilih Activity Group"
                        value={local.activity_group_id ?? ''}
                        onChange={e => set('activity_group_id', e.target.value ? Number(e.target.value) : undefined)}
                        isDisabled={coaList.length === 0}
                    >
                        {coaList.map(c => (
                            <option key={c.coa_id} value={c.coa_id}>{c.coa}</option>
                        ))}
                    </Select>
                </FormControl>

                {/* Activity */}
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>
                        <HStack spacing={1}>
                            <Text>Activity</Text>
                            {loadingActivity && <Spinner size="xs" />}
                        </HStack>
                    </FormLabel>
                    <Select
                        {...inputStyles}
                        placeholder="Pilih Activity"
                        value={local.activity_id ?? ''}
                        onChange={e => set('activity_id', e.target.value ? Number(e.target.value) : undefined)}
                        isDisabled={activityList.length === 0}
                    >
                        {activityList.map(a => (
                            <option key={a.activity_id} value={a.activity_id}>{a.activity}</option>
                        ))}
                    </Select>
                </FormControl>

                {/* Reference Activity */}
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>
                        Reference Activity
                    </FormLabel>
                    <Select
                        {...inputStyles}
                        placeholder="Pilih Reference Activity"
                        value={local.reference_activity_id ?? ''}
                        onChange={e => set('reference_activity_id', e.target.value ? Number(e.target.value) : undefined)}
                        isDisabled={activityList.length === 0}
                    >
                        {activityList.map(a => (
                            <option key={a.activity_id} value={a.activity_id}>{a.activity}</option>
                        ))}
                    </Select>
                </FormControl>

                {/* Tahun */}
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>Tahun</FormLabel>
                    <Select
                        {...inputStyles}
                        value={local.budget_year}
                        onChange={e => set('budget_year', e.target.value)}
                    >
                        {YEAR_OPTIONS.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </Select>
                </FormControl>

                {/* Bulan */}
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500" mb={1}>Sampai Bulan</FormLabel>
                    <Select
                        {...inputStyles}
                        placeholder="Semua Bulan"
                        value={local.budget_month ?? ''}
                        onChange={e => set('budget_month', e.target.value || undefined)}
                    >
                        {MONTH_OPTIONS.map(([val, label]) => (
                            <option key={val} value={val}>{label}</option>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Footer buttons */}
            <Box
                px={{ base: 3, md: 4 }}
                pt={{ base: 3, md: 4 }}
                pb="calc(12px + env(safe-area-inset-bottom, 0px))"
                borderTop="1px solid"
                borderColor="gray.100"
                flexShrink={0}
            >
                <Flex gap={{ base: 2, md: 3 }} direction={{ base: 'column', sm: 'row' }}>
                    <Button
                        leftIcon={<MdClear />}
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        flex={1}
                        onClick={handleClear}
                        w={{ base: 'full', sm: 'auto' }}
                    >
                        Hapus Filter
                    </Button>
                    <Button
                        leftIcon={<FiSearch />}
                        size="sm"
                        colorScheme="green"
                        flex={1}
                        onClick={handleApply}
                        isLoading={loading}
                        isDisabled={!local.activity_id}
                        w={{ base: 'full', sm: 'auto' }}
                    >
                        Terapkan Filter
                    </Button>
                </Flex>
            </Box>
        </Box>
    );
};

export default FilterUpkeepMap;
